import { useEffect, useState, useCallback } from 'react'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { ArrowClockwise, Boat, Airplane, Truck, Package } from '@phosphor-icons/react'
import { getShipperControlTower, ShipperTowerItem } from '../../services/shipperControlTower'
import { formatDateRelative } from '../../lib/date'

const COLUMNS: { key: ShipperTowerItem['stage']; label: string; color: string; bar: string }[] = [
  { key: 'awaiting_offers', label: 'Aguardando Propostas', color: 'text-gray-600', bar: 'bg-gray-400' },
  { key: 'receiving_offers', label: 'Recebendo Propostas', color: 'text-amber-600', bar: 'bg-amber-400' },
  { key: 'closed', label: 'Aguardando Embarque', color: 'text-emerald-600', bar: 'bg-emerald-500' },
  { key: 'in_transit', label: 'Em Trânsito', color: 'text-blue-600', bar: 'bg-blue-500' },
  { key: 'delivered', label: 'Entregue', color: 'text-purple-600', bar: 'bg-purple-500' },
]

function modalityIcon(m: string | null) {
  if (m === 'maritimo') return <Boat size={14} />
  if (m === 'aereo') return <Airplane size={14} />
  if (m === 'terrestre') return <Truck size={14} />
  return null
}

function money(v: number | null, c: string | null) {
  if (v == null) return null
  try {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: c || 'USD' }).format(v)
  } catch {
    return `${c || ''} ${v.toLocaleString('pt-BR')}`
  }
}

export default function ShipperControlTowerPage() {
  const [items, setItems] = useState<ShipperTowerItem[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  const load = useCallback(async () => {
    setRefreshing(true)
    try {
      const data = await getShipperControlTower()
      setItems(data.items)
    } catch (e) {
      console.error('Erro ao carregar torre de controle', e)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    load()
    const t = setInterval(load, 20000)
    return () => clearInterval(t)
  }, [load])

  const byStage = (stage: string) => items.filter((i) => i.stage === stage)

  return (
    <AppLayout>
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Package size={24} className="text-emerald-600" weight="duotone" />
              <h1 className="text-2xl font-semibold text-gray-900">Torre de Controle</h1>
              <span className="text-sm text-gray-400">{items.length} processos</span>
            </div>
            <Button variant="outline" onClick={load} disabled={refreshing} className="gap-2">
              <ArrowClockwise size={18} className={refreshing ? 'animate-spin' : ''} />
              Atualizar
            </Button>
          </div>
        </div>
      </header>

      <main className="px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="text-center text-gray-500 py-16">Carregando...</div>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {COLUMNS.map((col) => {
              const list = byStage(col.key)
              return (
                <div key={col.key} className="flex-shrink-0 w-72">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${col.bar}`} />
                      <h3 className={`text-sm font-semibold ${col.color}`}>{col.label}</h3>
                    </div>
                    <span className="text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">{list.length}</span>
                  </div>
                  <div className="space-y-2">
                    {list.length === 0 ? (
                      <div className="text-xs text-gray-400 text-center py-6 border border-dashed border-gray-200 rounded-lg">vazio</div>
                    ) : (
                      list.map((item) => (
                        <div key={item.id} className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-md transition-shadow">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-semibold text-gray-900">{item.txControl}</span>
                            <span className="text-xs text-gray-400 flex items-center gap-1">
                              {modalityIcon(item.modality)}
                            </span>
                          </div>
                          {item.agentName && (
                            <div className="flex items-center gap-1 text-xs text-gray-600 mb-0.5">
                              <Package size={13} className="text-gray-400" />
                              <span className="truncate">{item.agentName}</span>
                            </div>
                          )}
                          <div className="flex items-center justify-between mt-1">
                            {item.totalValue != null ? (
                              <span className="text-xs font-medium text-emerald-700">{money(item.totalValue, item.currency)}</span>
                            ) : item.offersCount != null ? (
                              <span className="text-xs text-gray-500">{item.offersCount} proposta{item.offersCount === 1 ? '' : 's'}</span>
                            ) : <span />}
                            <span className="text-[10px] text-gray-400">{formatDateRelative(item.updatedAt)}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </main>
    </AppLayout>
  )
}
